import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
  input,
  effect,
} from "@angular/core";
import { MatSort, Sort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";

/** Column type hints for sorting */
export type ColumnSortType = "string" | "number" | "date";

/** Column sorting configuration */
export interface ColumnSortConfig {
  [columnKey: string]: ColumnSortType;
}

@Component({
  selector: "app-table-grid",
  templateUrl: "./table-grid.component.html",
  styleUrls: ["./table-grid.component.scss"],
  standalone: false,
})
export class TableGridComponent implements OnInit, AfterViewInit {
  data = input<any[]>([]);
  headers = input<any[]>([]);
  footer = input<string>("");
  buttonViewText = input<string>("");
  customStyleColumn = input<string>("");
  customStylepProp = input<string>("");
  customBoolColumn = input<string>("");
  customStyle = input<string>("");
  displayedColumns = input<any[]>([]);
  columnsLink = input<string[]>([]);
  columnWidths = input<string[]>([]);
  isLoading = input<boolean>(true);
  usingAutoLoad = input<boolean>(false);
  skeletonRowsCount = input<number>(5);

  /** Enable sorting functionality - default false to not affect existing tables */
  enableSorting = input<boolean>(false);
  /** Column type configuration for type-aware sorting */
  columnSortTypes = input<ColumnSortConfig>({});
  /** Default sort column */
  defaultSortColumn = input<string>("");
  /** Default sort direction */
  defaultSortDirection = input<"asc" | "desc">("desc");

  /* output */
  @Output() buttonClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() rowClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() cellClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() sortChanged: EventEmitter<Sort> = new EventEmitter<Sort>();

  @ViewChild(MatSort) sort!: MatSort;

  selectedRow: any;
  public columnsToDisplay: any[] = [];
  public skeletonRows: any[] = []; // Array to drive skeleton loop
  public dataSource = new MatTableDataSource<any>();

  constructor() {
    // React to displayedColumns signal changes
    effect(() => {
      this.columnsToDisplay = this.displayedColumns().slice();
    });

    // React to skeletonRowsCount signal changes
    effect(() => {
      this.skeletonRows = new Array(this.skeletonRowsCount()).fill(null);
    });

    // React to data signal changes - update dataSource
    effect(() => {
      const newData = this.data();
      if (this.enableSorting()) {
        this.dataSource.data = newData;
      }
    });
  }

  ngOnInit(): void {
    this.columnsToDisplay = this.displayedColumns().slice();
    this.skeletonRows = new Array(this.skeletonRowsCount()).fill(null);
    if (this.enableSorting()) {
      this.dataSource.data = this.data();
    }
  }

  ngAfterViewInit(): void {
    if (this.enableSorting() && this.sort) {
      this.dataSource.sort = this.sort;
      this.setupSortingDataAccessor();

      // Apply default sort if specified
      if (this.defaultSortColumn()) {
        const defaultSortColumn = this.defaultSortColumn();
        const defaultSortDirection = this.defaultSortDirection();

        // Defer sort initialization to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
          if (!this.sort) {
            return;
          }

          this.sort.active = defaultSortColumn;
          this.sort.direction = defaultSortDirection;
          this.sort.sortChange.emit({
            active: defaultSortColumn,
            direction: defaultSortDirection,
          });
        });
      }
    }
  }

  /** Configure type-aware sorting based on columnSortTypes */
  private setupSortingDataAccessor(): void {
    const sortTypes = this.columnSortTypes();

    this.dataSource.sortingDataAccessor = (
      item: any,
      property: string,
    ): string | number => {
      const columnType = sortTypes[property] || "string";
      const value = item[property];

      switch (columnType) {
        case "number":
          // Support display fields (e.g. durationText) that should sort by a raw numeric field.
          if (property.endsWith("Text")) {
            const rawNumberField = property.replace(/Text$/, "");
            const rawNumberValue = item[rawNumberField];
            if (typeof rawNumberValue === "number") {
              return rawNumberValue;
            }
          }
          return typeof value === "number" ? value : parseFloat(value) || 0;
        case "date":
          // Handle date fields - look for raw date field if display field is used
          const rawDateField = property.replace("_local", "");
          const dateValue = item[rawDateField] || value;
          return dateValue ? new Date(dateValue).getTime() : 0;
        case "string":
        default:
          // Support display fields (e.g. durationText) that should sort by a raw numeric field.
          if (property.endsWith("Text")) {
            const rawNumberField = property.replace(/Text$/, "");
            const rawNumberValue = item[rawNumberField];
            if (typeof rawNumberValue === "number") {
              return rawNumberValue;
            }
          }
          return (value || "").toString().toLowerCase();
      }
    };
  }

  /** Handle sort change events */
  onSortChange(sort: Sort): void {
    this.sortChanged.emit(sort);
  }

  buttonClick(element: any) {
    this.buttonClicked.emit(element);
  }

  rowClick(row: any) {
    this.selectedRow = row;
    this.rowClicked.emit(row);
  }

  cellClick(element: any, column: any) {
    this.cellClicked.emit({ element: element, column: column });
  }

  getColumnMinWidth(index: number): string {
    const widths = this.columnWidths();
    if (widths && widths[index] && widths[index] !== "*") {
      return widths[index] + "px";
    }
    return "";
  }
}
